/**
 * @readonly
 * @typedef {Object} VehicleObjectModel
 * @property {number} AVERAGE_ENERGY_CONSUMPTION - The average energy consumption calculated in kWh/100km
 * @property {number} AVERAGE_FUEL_CONSUMPTION - The average fuel consumption calculated in l/100km
 * @property {string} COLOR - The color of the object
 * @property {Array<string>} CONTROL_FLAGS - A limited selection of control flags of the object
 * @property {number} COURSE - The current course of the object in degree (between 0 and 359)
 * @property {number} CUSTOM_VEHICLE_ENERGY_CONSUMPTION - The user configured value for energy consumption in kWh/100km
 * @property {number} CUSTOM_VEHICLE_FUEL_CONSUMPTION - The user configured value for fuel consumption in l/100km
 * @property {string} DESTINATION - The vehicle destination
 * @property {Position} DESTINATION_POSITION - The position of the destination
 * @property {string} ENERGY_CONSUMPTION_PREFERENCE - Indicates which field has the energy consumption information
 * @property {string} ETA - The ETA in ISO8601 format
 * @property {string} FUEL_TYPE - Fuel type - Deprecated, use fuelType in the ObjectDetails resource instead
 * @property {string} FUEL_USAGE_PREFERENCE - Indicates which field has the fuel consumption information
 * @property {number} GEAR_SHIFT_RPM - Optimum gear shift rpm
 * @property {boolean} IGNITION_ON - Indicates whether the ignition is on
 * @property {number} IMAGE_ID - Custom object image id
 * @property {boolean} IS_INACTIVE - Object status
 * @property {string} LAST_CONNECTED_TACHOGRAPH - The last connected tachograph device
 * @property {string} LAST_GPS_TIME - The last GPS time in ISO8601 format
 * @property {number} LAST_MODIFIED - The last-modified timestamp of the object
 * @property {string} LICENSE_PLATE - The license plate
 * @property {string} LICENSE_PLATE_COUNTRY_CODE - ISO3166 code of the country registering the license plate
 * @property {string} LINK_DEVICE_TYPE - The link device type of the object
 * @property {string} POSITION_ADDRESS_ID - The ID of the address at the position of the object
 * @property {Object} LOCATION_DESCRIPTION - Reverse geocoded location description
 * @property {string} LOGBOOK_MODE - The logbook mode, e.g. 'PRIVATE', 'BUSINESS', 'COMMUTE'
 * @property {string} NAME - The name of the object. Unique within an account
 * @property {string} NUMBER - The number of the object. Unique within an account
 * @property {string} OBJECT_CLASS - The class of an wfObject ('VEHICLE', 'TRAILER', 'EQUIPMENT', 'MACHINERY')
 * @property {string} OBJECT_ID - The ID of the object
 * @property {number} ODOMETER - The odometer of the vehicle in meter, with a precision of 100m, rounded down
 * @property {Position} POSITION - The current position of the object
 * @property {string} POSITION_ADDRESS_ID - The ID of the address at the position of the object
 * @property {Object} POSITION_DESCRIPTION - Reverse geocoded position descriptions
 * @property {string} POSITION_ORIGIN - Origin of the position data, (e.g. 'SATELLITE' or 'GPRS')
 * @property {number} SPEED - The speed of the vehicle at the last position in kph
 * @property {number} SPEED_LIMIT - The speed limit of the vehicle in kph
 * @property {boolean} STANDSTILL - Indicates whether the object is standing still (true) or not (false)
 * @property {string} STEALTH_MODE - The stealth mode ['ACTIVATED', 'DEACTIVATED']
 * @property {string} STEALTH_MODE_MSG_TIME - The stealth mode message time in ISO8601 format
 * @property {number} TELEMATIC_DEVICE_BATTERY_LEVEL_PERCENTAGE - The electric charging state of the asset in percent
 * @property {string} TRIP_MODE - Deprecated, use logbookMode instead!
 * @property {string} TYPE - The type of the object, e.g. ['ape', 'barge', 'bike', ...]
 * @property {VehicleGeneration} VEHICLE_GENERATION - Vehicle generation details
 * @property {VehicleUsageType} VEHICLE_USAGE_TYPE - The vehicle usage type for driver assignment
 * @property {string} VIN - The vehicle identification number
 */
export const VehicleObjectModel = {
    AVERAGE_ENERGY_CONSUMPTION: 'averageEnergyConsumption',
    AVERAGE_FUEL_CONSUMPTION: 'averageFuelConsumption',
    COLOR: 'color',
    CONTROL_FLAGS: 'controlFlags',
    COURSE: 'course',
    CUSTOM_VEHICLE_ENERGY_CONSUMPTION: 'customVehicleEnergyConsumption',
    CUSTOM_VEHICLE_FUEL_CONSUMPTION: 'customVehicleFuelConsumption',
    DESTINATION: 'destination',
    DESTINATION_POSITION: 'destinationPosition',
    ENERGY_CONSUMPTION_PREFERENCE: 'energyConsumptionPreference',
    ENGINE_TOTAL_FUEL_USAGE: 'engineTotalFuelUsage',
    ETA: 'eta',
    FUEL_TYPE: 'fuelType',
    FUEL_USAGE_PREFERENCE: 'fuelUsagePreference',
    GEAR_SHIFT_RPM: 'gearShiftRpm',
    IGNITION_ON: 'ignitionOn',
    IMAGE_ID: 'imageId',
    IS_INACTIVE: 'isInactive',
    LAST_CONNECTED_TACHOGRAPH: 'lastConnectedTachograph',
    LAST_GPS_TIME: 'lastGpsTime',
    LAST_MODIFIED: 'lastModified',
    LICENSE_PLATE: 'licensePlate',
    LICENSE_PLATE_COUNTRY_CODE: 'licensePlateCountryCode',
    LICENSE_PLATE_FEDERAL_STATE_CODE: 'licensePlateFederalStateCode',
    LINK_DEVICE_TYPE: 'linkDeviceType',
    LOCATION_DESCRIPTION: 'locationDescription',
    LOGBOOK_MODE: 'logbookMode',
    NAME: 'name',
    NUMBER: 'number',
    OBJECT_CLASS: 'objectClass',
    OBJECT_ID: 'objectId',
    ODOMETER: 'odometer',
    POSITION: 'position',
    POSITION_ADDRESS_ID: 'positionAddressId',
    POSITION_ORIGIN: 'queueClass',
    SPEED: 'speed',
    SPEED_LIMIT: 'speedLimit',
    STANDSTILL: 'standStill',
    STANDSTILL_MSG_TIME: 'standstillMsgTime',
    STEALTH_MODE: 'stealthMode',
    STEALTH_MODE_MSG_TIME: 'stealthModeMsgTime',
    TELEMATIC_DEVICE_BATTERY_CHARGING_STATE: 'telematicDeviceBatteryChargingState',
    TELEMATIC_DEVICE_BATTERY_LEVEL_PERCENTAGE: 'telematicDeviceBatteryLevelPercentage',
    TRIP_MODE: 'tripMode',
    TYPE: 'type',
    VEHICLE_GENERATION: 'vehicleGeneration',
    VEHICLE_USAGE_TYPE: 'vehicleUsageType',
    VIN: 'vin'
}
